import { useMemo } from 'react';
import type { ActionMeta, MultiValue, OnChangeValue, Options } from 'react-select';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';

import type { Option } from './types.ts';

interface UseFixedOptionsProps<TOption extends Option> {
  value?: readonly TOption[] | TOption | null;
  onChange?: (newValue: OnChangeValue<TOption, true>, actionMeta: ActionMeta<TOption>) => void;
  isOptionDisabled?: (option: TOption, selectValue: Options<TOption>) => boolean;
}

interface UseFixedOptionsReturn<TOption extends Option> {
  onChange: (newValue: OnChangeValue<TOption, true>, actionMeta: ActionMeta<TOption>) => void;
  isClearable: boolean;
  isOptionDisabled: (option: TOption, selectValue: Options<TOption>) => boolean;
}

export function useFixedOptions<TOption extends Option>({
  value,
  onChange,
  isOptionDisabled: isOptionDisabledProp,
}: UseFixedOptionsProps<TOption>): UseFixedOptionsReturn<TOption> | undefined {
  const fixedValues = useMemo<MultiValue<TOption>>(() => (Array.isArray(value) ? value.filter((option) => option.fixed) : []), [value]);

  const handleChange = useCallbackRef((newValue: OnChangeValue<TOption, true>, actionMeta: ActionMeta<TOption>) => {
    switch (actionMeta.action) {
      case 'clear':
        onChange?.(fixedValues, actionMeta);
        return;
      case 'deselect-option':
        if (actionMeta.option?.fixed) return;
        break;
      case 'remove-value':
      case 'pop-value':
        if (actionMeta.removedValue?.fixed) return;
        break;
    }
    onChange?.(newValue, actionMeta);
  });

  const isOptionDisabled = useCallbackRef((option: TOption, selectValue: Options<TOption>) => {
    if (option.fixed) {
      return true;
    }

    if (isOptionDisabledProp) {
      return isOptionDisabledProp(option, selectValue);
    }

    return false;
  });

  if (!fixedValues.length) {
    return undefined;
  }

  return {
    onChange: handleChange,
    isClearable: fixedValues.length < (Array.isArray(value) ? value.length : 0),
    isOptionDisabled,
  };
}
